import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import { useGame } from '../../context/GameContext';
import SafeIcon from '../../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';

const { FiArrowLeft, FiRefreshCw } = FiIcons;

function MemoryGame() {
  const navigate = useNavigate();
  const { state, dispatch } = useGame();
  const [cards, setCards] = useState([]);
  const [flippedCards, setFlippedCards] = useState([]);
  const [matchedCards, setMatchedCards] = useState([]);
  const [moves, setMoves] = useState(0);
  const [gameCompleted, setGameCompleted] = useState(false);
  const [level, setLevel] = useState(1);

  const cardSymbols = [
    '🦁', '🐯', '🐘', '🦒', '🐵', '🦓', '🦏', '🐆',
    '🦋', '🐝', '🐛', '🦗', '🐞', '🕷️', '🐜', '🦟',
    '🌸', '🌻', '🌺', '🌷', '🌹', '🌼', '💐', '🌿',
    '⭐', '🌙', '☀️', '🌈', '⚡', '❄️', '🔥', '💎'
  ];

  const generateCards = () => {
    const gridSize = state.age <= 8 ? 4 : state.age <= 10 ? 6 : 8;
    const numPairs = (gridSize * gridSize) / 2;
    
    const selectedSymbols = cardSymbols.slice(0, numPairs);
    const gameCards = [...selectedSymbols, ...selectedSymbols]
      .map((symbol, index) => ({
        id: index,
        symbol,
        isFlipped: false,
        isMatched: false
      }))
      .sort(() => Math.random() - 0.5);
    
    setCards(gameCards);
    setFlippedCards([]);
    setMatchedCards([]);
    setMoves(0);
    setGameCompleted(false);
  };

  useEffect(() => {
    generateCards();
  }, [state.age, level]);

  useEffect(() => {
    if (flippedCards.length === 2) {
      const [first, second] = flippedCards;
      if (cards[first].symbol === cards[second].symbol) {
        // Match found
        setTimeout(() => {
          setMatchedCards(prev => [...prev, first, second]);
          setFlippedCards([]);
          dispatch({ type: 'ADD_SCORE', payload: 20 });
        }, 1000);
      } else {
        // No match
        setTimeout(() => {
          setFlippedCards([]);
        }, 1500);
      }
      setMoves(prev => prev + 1);
    }
  }, [flippedCards, cards, dispatch]);

  useEffect(() => {
    if (matchedCards.length === cards.length && cards.length > 0) {
      setGameCompleted(true);
      dispatch({ type: 'GAME_COMPLETED' });
      // Bonus points for fewer moves
      const bonus = Math.max(0, 50 - moves) * 2;
      dispatch({ type: 'ADD_SCORE', payload: bonus });
    }
  }, [matchedCards, cards, moves, dispatch]);

  const handleCardClick = (index) => {
    if (flippedCards.length === 2 || 
        flippedCards.includes(index) || 
        matchedCards.includes(index)) {
      return;
    }
    setFlippedCards(prev => [...prev, index]);
  };

  const restartGame = () => {
    generateCards();
  };

  const nextLevel = () => {
    setLevel(prev => prev + 1);
    generateCards();
  };

  const gridSize = state.age <= 8 ? 4 : state.age <= 10 ? 6 : 8;

  if (gameCompleted) {
    return (
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        className="min-h-screen flex items-center justify-center p-4"
      >
        <div className="bg-white rounded-3xl p-8 shadow-2xl border-4 border-safari-200 text-center max-w-md">
          <div className="text-6xl mb-4">🧠</div>
          <h2 className="text-3xl font-kid text-jungle-700 mb-4">
            Memory Master!
          </h2>
          <p className="font-comic text-jungle-600 mb-4">
            Level {level} completed in {moves} moves!
          </p>
          <p className="font-comic text-jungle-600 mb-6">
            Your memory is getting stronger!
          </p>
          <div className="space-y-3">
            <motion.button
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              onClick={nextLevel}
              className="w-full bg-gradient-to-r from-purple-500 to-purple-600 text-white p-3 rounded-2xl font-kid text-lg shadow-lg hover:shadow-xl transition-all duration-200"
            >
              Next Level
            </motion.button>
            <motion.button
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              onClick={restartGame}
              className="w-full bg-gradient-to-r from-blue-500 to-blue-600 text-white p-3 rounded-2xl font-kid text-lg shadow-lg hover:shadow-xl transition-all duration-200"
            >
              Play Again
            </motion.button>
            <motion.button
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              onClick={() => navigate('/dashboard')}
              className="w-full bg-gradient-to-r from-safari-500 to-safari-600 text-white p-3 rounded-2xl font-kid text-lg shadow-lg hover:shadow-xl transition-all duration-200"
            >
              Back to Games
            </motion.button>
          </div>
        </div>
      </motion.div>
    );
  }

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="min-h-screen p-4"
    >
      <div className="max-w-4xl mx-auto">
        {/* Header */}
        <motion.div
          initial={{ y: -50, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          className="flex items-center justify-between mb-8"
        >
          <button
            onClick={() => navigate('/dashboard')}
            className="flex items-center space-x-2 bg-white rounded-2xl px-4 py-2 shadow-lg border-2 border-safari-200 hover:shadow-xl transition-all duration-200"
          >
            <SafeIcon icon={FiArrowLeft} className="text-jungle-600" />
            <span className="font-comic text-jungle-700">Back</span>
          </button>

          <div className="text-center">
            <h1 className="text-3xl font-kid text-jungle-700">Memory Match 🧠</h1>
            <p className="font-comic text-jungle-600">Level {level}</p>
          </div>

          <button
            onClick={restartGame}
            className="flex items-center space-x-2 bg-white rounded-2xl px-4 py-2 shadow-lg border-2 border-safari-200 hover:shadow-xl transition-all duration-200"
          >
            <SafeIcon icon={FiRefreshCw} className="text-jungle-600" />
            <span className="font-comic text-jungle-700">Restart</span>
          </button>
        </motion.div>

        {/* Stats */}
        <motion.div
          initial={{ y: 50, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          className="flex justify-center space-x-8 mb-8"
        >
          <div className="bg-white rounded-2xl px-4 py-2 shadow-lg border-2 border-safari-200">
            <span className="font-comic text-jungle-600">Moves: </span>
            <span className="font-kid text-jungle-700 text-xl">{moves}</span>
          </div>
          <div className="bg-white rounded-2xl px-4 py-2 shadow-lg border-2 border-safari-200">
            <span className="font-comic text-jungle-600">Pairs: </span>
            <span className="font-kid text-jungle-700 text-xl">{matchedCards.length / 2}/{cards.length / 2}</span>
          </div>
        </motion.div>

        {/* Game Grid */}
        <motion.div
          initial={{ scale: 0.8, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          className="flex justify-center"
        >
          <div 
            className="grid gap-3"
            style={{ 
              gridTemplateColumns: `repeat(${gridSize}, 1fr)`,
              maxWidth: '600px'
            }}
          >
            {cards.map((card, index) => (
              <motion.div
                key={card.id}
                initial={{ rotateY: 0 }}
                animate={{ 
                  rotateY: flippedCards.includes(index) || matchedCards.includes(index) ? 180 : 0 
                }}
                transition={{ duration: 0.6 }}
                whileHover={{ scale: flippedCards.includes(index) || matchedCards.includes(index) ? 1 : 1.05 }}
                whileTap={{ scale: 0.95 }}
                onClick={() => handleCardClick(index)}
                className="relative w-16 h-16 md:w-20 md:h-20 cursor-pointer"
                style={{ transformStyle: 'preserve-3d' }}
              >
                {/* Card Back */}
                <div
                  className="absolute inset-0 bg-gradient-to-br from-jungle-400 to-jungle-500 rounded-xl shadow-lg border-2 border-white flex items-center justify-center text-2xl"
                  style={{ 
                    backfaceVisibility: 'hidden',
                    transform: 'rotateY(0deg)'
                  }}
                >
                  🌿
                </div>
                
                {/* Card Front */}
                <div
                  className={`absolute inset-0 rounded-xl shadow-lg border-2 border-white flex items-center justify-center text-3xl ${
                    matchedCards.includes(index) 
                      ? 'bg-gradient-to-br from-green-400 to-green-500' 
                      : 'bg-gradient-to-br from-safari-200 to-safari-300'
                  }`}
                  style={{ 
                    backfaceVisibility: 'hidden',
                    transform: 'rotateY(180deg)'
                  }}
                >
                  {card.symbol}
                </div>
              </motion.div>
            ))}
          </div>
        </motion.div>
      </div>
    </motion.div>
  );
}

export default MemoryGame;